import {ArrowRight, CloudRain, Wind} from '@nlabs/gothamjs/icons';
import {Link} from '@nlabs/gothamjs/router';
import {useMemo} from 'react';
import {ArticleLink} from '../components/ArticleLink';
import {ArticleImage} from '../components/ArticleImage';
import {NewsFlash} from '../components/NewsFlash';
import {SiteFooter} from '../components/SiteFooter';
import {SiteHeader} from '../components/SiteHeader';
import {ShopPromotion} from '../components/ShopPromotion';
import {StoryCard} from '../components/StoryCard';
import {useStoreState} from '../stores/useStoreState';
import type {Article} from '../types/article';
import {composeHomepageArticles} from '../domain/homepageComposition';
import type {WeatherSnapshot} from '@chicago-sentinel/contracts/weather';

const weatherDayLabel = (date: string, index: number): string => {
  if(index === 0) return 'Today';
  return new Intl.DateTimeFormat('en-US', {timeZone: 'America/Chicago', weekday: 'short'})
    .format(new Date(`${date}T12:00:00-05:00`));
};

const weatherUpdateLabel = (updatedAt: string): string =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Chicago'
  }).format(new Date(updatedAt));

export const HomePage = () => {
  const liveArticles = useStoreState<Article[]>('homepage.liveArticles', []);
  const displayedArticles = useMemo(() => composeHomepageArticles(liveArticles), [liveArticles]);
  const feedStatus = useStoreState<'error' | 'live' | 'loading'>('homepage.status', 'loading');
  const weather = useStoreState<WeatherSnapshot | undefined>('homepage.weather', undefined);
  const weatherStatus = useStoreState<'error' | 'live' | 'loading'>('homepage.weatherStatus', 'loading');
  const [lead, ...secondaryArticles] = displayedArticles;
  const aiStory = displayedArticles.find((article) => article.isEditorial && article.section === 'Science & Technology' && !article.tags.includes('phantom-dark'));

  return (
  <div className="newspaper">
    <SiteHeader />
    <NewsFlash />
    <main id="main-content">
      {!lead ? (
        <section className="shell article-feed-state" aria-live="polite">
          <span className="eyebrow">Stored edition</span>
          <h1>{feedStatus === 'error' ? 'The article service is unavailable.' : feedStatus === 'live' ? 'No stored articles yet.' : 'Loading stored articles…'}</h1>
          <p>{feedStatus === 'error' ? 'Check the local API and database connection, then reload the page.' : 'Only articles returned from the database will appear here.'}</p>
        </section>
      ) : null}
      {lead ? (
      <section className="shell lead-grid" aria-label="Top stories">
        <aside className="headline-rail">
          <div className="section-kicker"><span>More headlines</span></div>
          {secondaryArticles.slice(0, 3).map((article, index) => (
            <article className="rail-story" key={article.id}>
              <span>0{index + 1}</span>
              <h2><ArticleLink article={article}>{article.headline}</ArticleLink></h2>
            </article>
          ))}
        </aside>
        <article className="lead-story">
          {lead.image ? (
            <ArticleLink article={lead} className="lead-story__image">
              <ArticleImage image={lead.image} fetchPriority="high" />
            </ArticleLink>
          ) : null}
          <div className="lead-story__copy">
            <span className="story-card__section">{lead.section}</span>
            <h1><ArticleLink article={lead}>{lead.headline}</ArticleLink></h1>
            <p>{lead.description}</p>
            <div className="byline">
              {lead.sourceType === 'external' ? `Source: ${lead.sourceName}` : 'By The Sentinel City Desk'}
              <span>•</span> {new Date(lead.publishedAt).toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})}
            </div>
          </div>
        </article>
        <aside className="popular-column">
          <div className="section-kicker"><span>Most read</span></div>
          {aiStory ? <StoryCard article={aiStory} compact /> : null}
          <div className="popular-list">
            {secondaryArticles.slice(0, 3).map((article, index) => (
              <ArticleLink article={article} key={article.id}><span>{index + 1}</span>{article.headline}</ArticleLink>
            ))}
          </div>
        </aside>
      </section>
      ) : null}

      {secondaryArticles.length ? <section className="shell content-section" id="chicago">
        <div className="section-heading"><h2>Chicago Now</h2><a href="#latest">See all local news <ArrowRight size={16} /></a></div>
        <div className="story-grid story-grid--three">
          {secondaryArticles.slice(0, 3).map((article) => <StoryCard article={article} key={article.id} />)}
        </div>
      </section> : null}

      <ShopPromotion />

      <section className="weather-section" id="weather">
        <div className="shell weather-grid">
          <div className="weather-intro">
            <span className="eyebrow">Chicago Weather</span>
            <h2>{weather ? `${weather.summary} in Chicago.` : weatherStatus === 'error' ? 'Weather is temporarily unavailable.' : 'Loading current Chicago weather…'}</h2>
            <p>{weather ? <>{weather.stale ? 'Cached conditions' : 'Conditions'} updated at {weatherUpdateLabel(weather.updatedAt)}.</> : 'The news remains available while weather data loads.'}</p>
          </div>
          <div className="current-weather"><span>{weather ? `${weather.currentTemperatureF}°` : '—'}</span><div>{weather?.summary ?? 'No current reading'}<small>{weather ? `Feels like ${weather.apparentTemperatureF}°` : 'Please check again shortly'}</small></div></div>
          <div className="weather-stat"><CloudRain size={24} /><strong>{weather ? `${weather.precipitationChance}%` : '—'}</strong><span>Rain</span></div>
          <div className="weather-stat"><Wind size={24} /><strong>{weather ? `${weather.windDirection} ${weather.windSpeedMph}` : '—'}</strong><span>mph</span></div>
          <div className="forecast">{weather?.forecast.map((day, index) => <span key={day.date}>{weatherDayLabel(day.date, index)}<strong>{day.highF}° / {day.lowF}°</strong></span>)}</div>
        </div>
      </section>

      {aiStory ? <section className="shell feature-split" id="science-&-technology">
        {aiStory.image ? <div className="feature-split__image"><ArticleImage image={aiStory.image} /></div> : null}
        <article className="feature-split__copy">
          <span className="story-card__section">Science & Technology</span>
          <h2><ArticleLink article={aiStory}>{aiStory.headline}</ArticleLink></h2>
          <p>{aiStory.description}</p>
          <div className="byline">By Mara Ellison <span>•</span> Sentinel Investigation</div>
          <ArticleLink article={aiStory} className="text-link">Continue reading <ArrowRight size={16} /></ArticleLink>
        </article>
      </section> : null}

      {displayedArticles.length ? <section className="shell content-section" id="latest">
        <div className="section-heading"><h2>Latest</h2><span>Current stored edition</span></div>
        <div className="latest-list">
          {displayedArticles.slice(0, 6).map((article) => (
            <article key={article.id}><time>{new Date(article.publishedAt).toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})}</time><div><span>{article.section}</span><h3><ArticleLink article={article}>{article.headline}</ArticleLink></h3></div></article>
          ))}
        </div>
        <div className="archive-link"><Link to="/archive">Browse the article archive</Link></div>
      </section> : null}
    </main>
    <SiteFooter />
  </div>
  );
};
